<template>
    <v-container fluid fill-height>
        <v-layout justify-center wrap row>
            <v-form ref="form" v-model="valid">
                <v-btn
                        class="mx-0 btn-cancel"
                        small
                        :disabled="loading"
                        @click="cancel"
                        outline
                        fab
                >
                    <v-icon>arrow_back</v-icon>
                </v-btn>

                <h1 v-if="$vuetify.breakpoint.xs" class="display-1">Tänzer/in</h1>
                <h1 v-else class="display-1">{{ dancer.id ? $t('edit_dancer') : $t('create_dancer') }}</h1>

                <v-btn
                        class="btn-save mx-0"
                        color="primary"
                        @click="submit"
                        :disabled="!valid || !dancer.birth_date || loading"
                        :large="!$vuetify.breakpoint.xs"
                        round
                >{{ $t('save') }}</v-btn>
                <v-btn
                        class="btn-save mr-1"
                        :large="!$vuetify.breakpoint.xs"
                        v-show="!$vuetify.breakpoint.xs && dancer.id === null"
                        @click="submit(false)"
                        :disabled="!valid || !dancer.birth_date || loading"
                        round
                >{{ $t('save_and_new') }}</v-btn>

                <v-alert v-if="dancer.needs_birthday" icon="warning" type="warning" :value="true">{{ $t('alerts.fix_dancer_birthday') }}</v-alert>
                <v-radio-group v-model="dancer.sex" :disabled="loading" :loading="loading" :rules="rules.gender" row>
                    <v-radio :label="$t('female')" value="female"></v-radio>
                    <v-radio :label="$t('male')" value="male"></v-radio>
                    <v-radio :label="$t('non_binary')" value="non_binary"></v-radio>
                </v-radio-group>

                <v-text-field
                        class="mt-0"
                        :label="$t('first_name')"
                        :loading="loading"
                        :disabled="loading"
                        v-model.trim="dancer.first_name"
                        :rules="rules.firstName"
                        :counter="255"
                        @keyup.enter="submit"
                        required
                        outline
                ></v-text-field>

                <v-text-field
                        :label="$t('last_name')"
                        :loading="loading"
                        :disabled="loading"
                        v-model.trim="dancer.last_name"
                        :rules="rules.lastName"
                        :counter="255"
                        @keyup.enter="submit"
                        required
                        outline
                ></v-text-field>

                <date-picker
                        :value.sync="dancer.birth_date"
                        birth-date="birth-date"
                        :max="maxBirthDate"
                        :loading="loading"
                        :disabled="loading"
                        :label="$t('birth_date_entry')"
                        :rules="rules.birthDate"
                        @keyup.enter="submit"
                        required
                        outline
                ></date-picker>

                <data-protection-agreement v-show="needAgreement" :readonly="!!dancer.id && agreement && dancer.agreement_confirmed" :required="needAgreement" :value.sync="agreement"></data-protection-agreement>
            </v-form>
        </v-layout>
    </v-container>
</template>

<script>
    import { mapState } from 'vuex';
    import DatePicker from '../common/DatePicker';
    import DataProtectionAgreement from '../common/DataProtectionAgreement';

    export default {
        components: {
            DatePicker,
            DataProtectionAgreement
        },
        props: {
            id: {
                type: String,
                default: null
            }
        },
        computed: {
            needAgreement() {
                if (!this.dancer.birth_date || this.dancer.birth_date === '') {
                    return false;
                }

                return moment().diff(moment(this.dancer.birth_date), 'years') < 18;
            },
            ...mapState(['dancers'])
        },
        data () {
            return {
                $validates: true,
                agreement: false,
                maxBirthDate: moment().subtract(2, 'years').format('YYYY-MM-DD'),
                dancer: {
                    id: null,
                    sex: 'unknown',
                    first_name: '',
                    last_name: '',
                    birth_date: '',
                    needs_birthday: false,
                    agreement_confirmed: false
                },
                loading: false,
                valid: false,
                rules: {
                    firstName: [
                        (v) => !!v || 'Vorname fehlt'
                    ],
                    lastName: [
                        (v) => !!v || 'Nachname fehlt'
                    ],
                    birthDate: [
                        (v) => !!v || 'Geburtsdatum fehlt'
                    ],
                    gender: [
                        (v) => (!!v && ['female', 'male', 'non_binary'].includes(v)) || 'Geschlecht fehlt'
                    ]
                },
                modal: false
            }
        },
        mounted() {
            if (this.id) {
                this.loadDancer(this.id);
            }
        },
        methods: {
            loadDancer(id) {
                this.loading = true;

                this.dancer.id = parseInt(id);

                axios.get(`/api/v1/dancers/${id}`).then(response => {

                    if (!response.data.success) {
                        this.$store.commit('snackBar', {
                            color: 'error',
                            text: 'Tänzer konnte nicht geladen werden'
                        });
                        return;
                    }

                    this.dancer.first_name = response.data.data.first_name;
                    this.dancer.last_name = response.data.data.last_name;
                    this.dancer.sex = response.data.data.sex;
                    this.dancer.birth_date = response.data.data.birth_date;
                    this.dancer.needs_birthday = response.data.data.needs_birthday;
                    this.dancer.agreement_confirmed = response.data.data.agreement_confirmed;

                    if (this.dancer.agreement_confirmed || !this.needAgreement) {
                        this.$set(this, 'agreement', true);
                    }

                    this.loading = false;

                    this.$refs.form.validate();
                }, errorResponse => {
                    console.log(errorResponse);
                    this.loading = false;
                });
            },
            submit (forward = true) {
                if (!this.valid || this.loading) {
                    return;
                }

                this.loading = true;

                let dancer = _.cloneDeep(this.dancer);
                if (this.agreement) {
                    dancer.agreement_confirmed = true;
                }

                axios.post('/api/v1/dancers', dancer).then(response => {

                    if (!response.data.success) {
                        this.$store.commit('snackBar', {
                            color: 'error',
                            text: this.$t(`errors.${response.data.message}`)
                        });
                        this.loading = false;
                        return;
                    }

                    if (this.dancer.id) {
                        this.$store.commit('updateDancer', response.data.data);
                    } else {
                        this.$store.commit('addDancer', response.data.data);
                    }

                    this.$store.dispatch('loadStarterTemplates');

                    if (forward) {
                        this.$router.push('/user/dancer');
                    } else {
                        this.$refs.form.reset();
                    }

                    this.loading = false;

                    this.$store.commit('snackBar', {
                        color: 'success',
                        text: 'Tänzer erfolgreich gespeichert'
                    });

                }, errorResponse => {
                    console.log(errorResponse);
                    this.loading = false;
                });
            },
            cancel () {
                this.$router.go(-1);
            }
        }
    }
</script>

<style scoped>
    form {
        width: 100%;
    }

    .btn-save {
        float: right;
        margin-top: 2px;
    }

    .btn-cancel {
        margin-top: -10px;
    }

    h1 {
        display: inline-block;
        margin-bottom: 18px;
        margin-left: 10px;
    }
</style>
